import React, { useEffect, useId, useRef, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { ThemeMode, ResolvedTheme, formatLabel, getThemeLabel } from './theme';

const themeOptions: Array<{ mode: ThemeMode; label: string; icon: React.ReactNode }> = [
  { mode: 'light', label: 'Light', icon: <Sun size={16} aria-hidden="true" /> },
  { mode: 'dark', label: 'Dark', icon: <Moon size={16} aria-hidden="true" /> },
  { mode: 'system', label: 'System', icon: <Monitor size={16} aria-hidden="true" /> },
];

const ThemeIcon: React.FC<{ mode: ThemeMode; resolved: ResolvedTheme }> = ({ mode, resolved }) => {
  if (mode === 'system') return <Monitor size={18} aria-hidden="true" />;
  if (resolved === 'dark') return <Moon size={18} aria-hidden="true" />;
  return <Sun size={18} aria-hidden="true" />;
};

interface ThemeDropdownProps {
  currentMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  onSelect: (mode: ThemeMode) => void;
  variant?: 'desktop' | 'mobile';
}

const ThemeDropdown: React.FC<ThemeDropdownProps> = ({
  currentMode,
  resolvedTheme,
  onSelect,
  variant = 'desktop',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuId = useId();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const menuItems = menuRef.current?.querySelectorAll('button');
    if (!menuItems) return;

    const currentIndex = Array.from(menuItems).findIndex(
      (item) => item === document.activeElement,
    );

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          menuItems[0]?.focus();
        } else {
          const nextIndex = currentIndex < menuItems.length - 1 ? currentIndex + 1 : 0;
          menuItems[nextIndex]?.focus();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          const prevIndex = currentIndex > 0 ? currentIndex - 1 : menuItems.length - 1;
          menuItems[prevIndex]?.focus();
        }
        break;
      default:
        break;
    }
  };

  const handleSelect = (mode: ThemeMode) => {
    onSelect(mode);
    setIsOpen(false);
  };

  const currentLabel = getThemeLabel(currentMode, resolvedTheme);

  if (variant === 'mobile') {
    return (
      <div className="theme-dropdown-mobile">
        <div className="theme-dropdown-mobile-label">
          <ThemeIcon mode={currentMode} resolved={resolvedTheme} />
          <span>Theme</span>
        </div>
        <div className="theme-options-row" role="radiogroup" aria-label="Theme">
          {themeOptions.map((option) => (
            <button
              key={option.mode}
              type="button"
              className={`theme-option-btn ${currentMode === option.mode ? 'theme-option-active' : ''}`}
              onClick={() => handleSelect(option.mode)}
              aria-checked={currentMode === option.mode}
              role="radio"
            >
              {option.icon}
              <span>{option.label}</span>
              {option.mode === 'system' && (
                <span className="theme-option-meta">{formatLabel(resolvedTheme)}</span>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={dropdownRef}
      className={`theme-dropdown ${isOpen ? 'theme-dropdown-open' : ''}`}
      onKeyDown={handleKeyDown}
    >
      <button
        ref={buttonRef}
        type="button"
        className="theme-toggle-btn"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={menuId}
        aria-label={`Theme: ${currentLabel}`}
      >
        <ThemeIcon mode={currentMode} resolved={resolvedTheme} />
      </button>
      <ul
        ref={menuRef}
        id={menuId}
        className="theme-dropdown-menu"
        role="listbox"
        aria-label="Select theme"
      >
        {themeOptions.map((option) => (
          <li key={option.mode} role="option" aria-selected={currentMode === option.mode}>
            <button
              type="button"
              className={`theme-dropdown-item ${currentMode === option.mode ? 'theme-dropdown-item-active' : ''}`}
              onClick={() => handleSelect(option.mode)}
            >
              {option.icon}
              <span>{option.label}</span>
              {option.mode === 'system' && (
                <span className="theme-option-meta">{formatLabel(resolvedTheme)}</span>
              )}
              {currentMode === option.mode && (
                <svg
                  className="theme-check"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ThemeDropdown;
